import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { SessionStorageService } from '../helpers/session-storage.service';
import { UserActivityService } from '../services/user-activity.service';

const TOKEN_HEADER_KEY = 'Authorization';

function isAuthPage(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return path === '/login' || path === '/register';
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Inject services inside the function
  const sessionStorageService = inject(SessionStorageService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const userActivityService = inject(UserActivityService);

  // Exclude specific URLs from interception
  if (req.url.includes('traccar') || req.url.includes('8082')) {
    return next(req);
  }

  // Get the token and add the Authorization header when available
  const token = sessionStorageService.getAccessToken();
  let authReq = req;

  if (token) {
    authReq = req.clone({
      headers: req.headers.set(TOKEN_HEADER_KEY, `Bearer ${token}`),
    });
  }

  // Handle the request and catch errors
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // If the error is not a 401, just pass it along
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // If it is a 401, check if user is active
      if (!userActivityService.isActive() || !authService.hasCurrentUser()) {
        if (!isAuthPage(router.url)) {
          console.warn('User away or no session, logging out and redirecting to login');
          authService.logout().subscribe(() => {
            sessionStorageService.signOut();
            router.navigate(['/login']);
          });
        }
        return throwError(() => error);
      }

      // User is active, attempt to refresh the token
      return authService.getCurrentUserAccessToken().pipe(
        switchMap((newAccessToken: string) => {
          // 1. Save the new token
          sessionStorageService.saveAccessToken(newAccessToken, new Date().getTime());

          // 2. Clone the original request with the new token
          const newAuthReq = req.clone({
            headers: req.headers.set(TOKEN_HEADER_KEY, `Bearer ${newAccessToken}`),
          });

          // 3. Retry the request with the new token
          return next(newAuthReq);
        }),
        catchError((refreshError) => {
          // If the refresh fails, logout and redirect to login
          console.error('Token refresh failed:', refreshError);
          if (!isAuthPage(router.url)) {
            authService.logout().subscribe(() => {
              sessionStorageService.signOut();
              router.navigate(['/login']);
            });
          }
          return throwError(() => refreshError);
        })
      );
    })
  );
};
